import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Sparkles, X, Minus, Send, Mic, Bot, User, 
  CheckCircle2, Loader2, Tag, Percent, Calendar, 
  Zap, Box, Archive, DollarSign, Package, AlertCircle,
  RefreshCw, Trash2
} from 'lucide-react';
import { useAIStore, ChatMessage } from '../stores/useAIStore';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import { useApiMutation } from '../hooks/useApi';
import { toast } from 'sonner';

// ─── Action Display Config ───
const ACTION_DISPLAY: Record<string, { icon: any; color: string; label: string }> = {
  CREATE_DISCOUNT: { icon: Tag, color: 'text-emerald-500', label: 'Create Discount Code' },
  UPDATE_PRODUCTION: { icon: Box, color: 'text-blue-500', label: 'Update Production Stage' },
  UPDATE_ORDER: { icon: Zap, color: 'text-blue-500', label: 'Update Order' },
  ARCHIVE_LEAD: { icon: Archive, color: 'text-slate-500', label: 'Archive Lead' },
  UPDATE_INVENTORY: { icon: Package, color: 'text-amber-500', label: 'Adjust Inventory' },
  MARK_PAID: { icon: DollarSign, color: 'text-emerald-600', label: 'Finalize Payout' },
  CREATE_TASK: { icon: CheckCircle2, color: 'text-violet-500', label: 'Create New Task' },
  DELETE_RECORD: { icon: Trash2, color: 'text-red-500', label: 'Delete Record' },
  CREATE_BLOG_POST: { icon: RefreshCw, color: 'text-admin-forest', label: 'Draft Blog Post' },
};

export default function AICoPilotWidget() {
  const {
    isWidgetOpen, setIsWidgetOpen,
    isWidgetMinimized, setIsWidgetMinimized,
    chatHistory, addMessage, userRole
  } = useAIStore();
  
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [executingAction, setExecutingAction] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const location = useLocation();
  const { session } = useAdminAuthStore();

  // ─── SpeechRecognition via useRef ───
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SR) {
      recognitionRef.current = new SR();
    }
    return () => {
      try { recognitionRef.current?.abort(); } catch (_) {}
    };
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, isWidgetOpen, isWidgetMinimized]);

  // ─── Toggle Microphone ───
  const toggleMic = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      toast.error("Your browser does not support voice input. Please use Chrome.");
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.onstart = () => setIsListening(true);
      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          setInputText(prev => prev + (prev ? ' ' : '') + finalTranscript);
        }
      };
      recognition.onerror = (event: any) => { console.error("Speech error", event.error); setIsListening(false); };
      recognition.onend = () => setIsListening(false);
      recognition.start();
    }
  }, [isListening]);

  // ─── Send Message to Backend ───
  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMsg = inputText.trim();
    setInputText('');

    addMessage({ role: 'user', content: userMsg, type: 'text' });
    setIsLoading(true);

    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiBase}/admin/ai/copilot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          actionType: 'chat',
          payload: {
            currentMessage: userMsg,
            chatHistory: chatHistory.slice(-10).map(m => ({ role: m.role, content: m.content || m.summary })),
            context: {
              page: location.pathname,
              userRole: userRole
            }
          }
        })
      });

      const data = await res.json();
      if (data.success && data.data) {
        addMessage({
          role: 'assistant',
          type: data.data.type || 'text',
          content: data.data.content,
          action: data.data.action,
          summary: data.data.summary,
          payload: data.data.payload
        });
      } else {
        addMessage({ role: 'assistant', type: 'text', content: `⚠️ ${data.error || 'I encountered an issue processing that.'}` });
      }
    } catch (err: any) {
      console.error('[Max] Connection error:', err);
      addMessage({ role: 'assistant', type: 'text', content: `⚠️ Connection to Intelligence Hub failed.` });
    }

    setIsLoading(false);
  };

  // ─── Execute Proposed Actions ───
  const executeAction = async (msgIndex: number, actionName: string, payload: any) => {
    setExecutingAction(msgIndex);
    
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiBase}/admin/ai/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ action: actionName, payload })
      });

      const data = await res.json();
      if (data.success) {
        addMessage({ 
          role: 'assistant', 
          type: 'text', 
          content: `✅ Action executed successfully: ${data.message || 'Operation completed.'}` 
        });
        toast.success(`Executed: ${actionName}`);
      } else {
        throw new Error(data.error || 'Action failed');
      }
    } catch (err: any) {
      console.error('[Max] Action execution error:', err);
      addMessage({ role: 'assistant', type: 'text', content: `⚠️ Failed to execute ${actionName}: ${err.message}` });
    }
    
    setExecutingAction(null);
  };

  if (!isWidgetOpen) {
    return (
      <button
        onClick={() => { setIsWidgetOpen(true); setIsWidgetMinimized(false); }}
        className="fixed bottom-8 right-8 z-50 w-14 h-14 rounded-full shadow-2xl shadow-admin-forest/30 flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-all duration-200 group overflow-hidden"
        aria-label="Open Max Assistant"
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-admin-forest to-emerald-600 transition-opacity group-hover:opacity-90" />
        <Sparkles className="text-white relative z-10 w-6 h-6" />
        <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-20" />
      </button>
    );
  }

  if (isWidgetMinimized) {
    return (
      <div
        onClick={() => setIsWidgetMinimized(false)}
        className="fixed bottom-8 right-8 z-50 flex items-center gap-3 bg-gradient-to-r from-admin-forest to-emerald-600 text-white pl-4 pr-5 py-3 rounded-full shadow-2xl shadow-admin-forest/30 cursor-pointer hover:scale-105 active:scale-95 transition-all duration-200"
      >
        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
          <Sparkles className="text-white w-4 h-4" />
        </div>
        <span className="text-sm font-bold tracking-tight">Max AI</span>
        <button
          onClick={(e) => { e.stopPropagation(); setIsWidgetOpen(false); setIsWidgetMinimized(false); }}
          className="ml-1 p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-8 right-8 w-[380px] max-h-[560px] h-[calc(100vh-120px)] bg-white border border-gray-100 rounded-[2.5rem] shadow-2xl flex flex-col z-50 overflow-hidden animate-in slide-in-from-bottom-10 duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-admin-forest to-emerald-700 px-6 py-5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-sm">
            <Bot className="text-white w-5 h-5" />
          </div>
          <div>
            <h3 className="text-white text-sm font-black tracking-tight">MAX <span className="opacity-70 font-medium">Assistant</span></h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <p className="text-emerald-100 text-[9px] uppercase font-black tracking-widest leading-none">Intelligence Online</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsWidgetMinimized(true)} className="p-2 hover:bg-white/20 text-white/80 hover:text-white rounded-xl transition-colors">
            <Minus size={18} />
          </button>
          <button onClick={() => { setIsWidgetOpen(false); setIsWidgetMinimized(false); }} className="p-2 hover:bg-white/20 text-white/80 hover:text-white rounded-xl transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/50">
        {chatHistory.length === 0 ? (
          <div className="text-center mt-8 space-y-5">
            <div className="w-16 h-16 bg-white border border-gray-100 text-admin-forest rounded-[1.5rem] flex items-center justify-center mx-auto shadow-xl">
              <Sparkles size={32} />
            </div>
            <div>
              <p className="text-lg font-black text-admin-text tracking-tight">I am Max AI</p>
              <p className="text-[11px] text-admin-text-muted mt-2 max-w-[240px] mx-auto leading-relaxed font-bold uppercase tracking-wider">Your personal Ayurvedic Business Assistant.</p>
            </div>
            <div className="space-y-2 pt-4 px-4">
              {[
                'Show me today\'s revenue',
                'Are there any pending reviews?',
                'Draft a blog about Ashwagandha',
                'Create a 15% discount code'
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => { setInputText(suggestion); }}
                  className="block w-full text-left px-5 py-3 bg-white border border-gray-100 rounded-2xl text-[10px] font-black uppercase tracking-widest text-admin-text-muted hover:border-admin-forest hover:text-admin-forest hover:shadow-md transition-all"
                >
                  <span className="text-admin-forest/40 mr-2">✦</span> {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          chatHistory.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`shrink-0 w-8 h-8 rounded-xl flex items-center justify-center border shadow-sm ${msg.role === 'user' ? 'bg-white border-gray-200 text-gray-600' : 'bg-admin-forest border-admin-forest text-white'}`}>
                {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
              </div>
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-admin-text text-white rounded-2xl rounded-tr-none' : 'bg-white border border-gray-100 text-admin-text rounded-2xl rounded-tl-none shadow-sm'} px-4 py-3`}>
                
                {(msg.type === 'text' || !msg.type) && (
                  <p className="text-xs font-bold leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                )}

                {msg.type === 'proposed_action' && (() => {
                  const actionConfig = ACTION_DISPLAY[msg.action?.toUpperCase() || ''] || {
                    icon: Sparkles, color: 'text-admin-forest', label: msg.action || 'Action'
                  };
                  const ActionIcon = actionConfig.icon;

                  return (
                    <div className="mt-2 space-y-3 border-t border-gray-50 pt-3">
                      <div className="flex items-center gap-2">
                        <ActionIcon size={14} className={actionConfig.color} />
                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">{actionConfig.label}</p>
                      </div>
                      
                      <p className="text-[11px] font-black text-admin-text leading-tight">{msg.summary}</p>
                      
                      {msg.payload && (
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-1">
                          {Object.entries(msg.payload).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-center gap-4">
                              <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest shrink-0">{key.replace(/_/g, ' ')}</span>
                              <span className="text-[10px] font-bold text-admin-text truncate">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        onClick={() => executeAction(idx, msg.action || '', msg.payload)}
                        disabled={executingAction === idx}
                        className="w-full bg-admin-forest hover:bg-admin-forest-dark text-white px-4 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-admin-forest/20 flex justify-center items-center gap-2 disabled:opacity-60"
                      >
                        {executingAction === idx ? (
                          <><Loader2 size={14} className="animate-spin" /> Executing...</>
                        ) : (
                          <><CheckCircle2 size={14} /> Confirm Action</>
                        )}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex gap-3 flex-row items-center">
            <div className="shrink-0 w-8 h-8 rounded-xl flex items-center justify-center bg-admin-forest border border-admin-forest text-white shadow-sm">
              <Bot size={14} />
            </div>
            <div className="bg-white border-gray-100 border rounded-2xl px-4 py-3 shadow-sm flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-admin-forest rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-admin-forest rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-admin-forest rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Box */}
      <div className="p-6 bg-white border-t border-gray-100">
        <div className="flex items-center gap-2 bg-gray-50 border-2 border-gray-100 rounded-2xl p-1 focus-within:border-admin-forest focus-within:bg-white transition-all">
          <button
            onClick={toggleMic}
            className={`p-2.5 rounded-xl transition-all ${isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-white text-gray-400 hover:text-admin-forest shadow-sm'}`}
          >
            <Mic size={18} />
          </button>
          <input
            type="text"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
            placeholder={isListening ? 'Listening...' : 'Message Max...'}
            className="flex-1 bg-transparent border-none focus:outline-none text-[11px] font-bold text-admin-text placeholder:text-gray-300 px-2"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading}
            className="p-2.5 bg-admin-text text-white rounded-xl shadow-md hover:bg-admin-text/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
